// How long Swym may stay silent before wishlist hearts are hidden on the current page.
export const SWYM_LOAD_DEADLINE_MS = 4000;

// How long into the page load `_swat` may stay missing before the failure is remembered
// for later pages. Longer than `SWYM_LOAD_DEADLINE_MS`, so a slow script is not mistaken
// for an outage.
const SWYM_GIVE_UP_MS = 10000;

// `data-swym` as it was when this module was evaluated. A page that starts unavailable
// keeps its hearts hidden even if Swym answers, so nothing pops in mid-visit.
const startedUnavailable =
  document.documentElement.dataset.swym === 'unavailable';

let watching = false;

/**
 * Hides every wishlist heart on this page and, when given a reason, remembers the failure
 * for later page loads. Does nothing once Swym has answered on this page: a later failure
 * is transient.
 * @param {'error' | 'missing' | null} reason `null` hides for this page only.
 */
export const markSwymUnavailable = (reason) => {
  if (window.swymLoaded) return;

  document.documentElement.dataset.swym = 'unavailable';

  const config = window.swymUnavailable;
  if (!reason || !config) return;

  try {
    window.localStorage.setItem(config.key, JSON.stringify({ at: Date.now() }));
  } catch (_) {
    // Storage blocked: the failure is only known to this page.
  }
};

/**
 * Arms the load timers, once per page: hearts hide `SWYM_LOAD_DEADLINE_MS` after the first
 * call if Swym hasn't answered, and the failure is remembered if `_swat` still hasn't
 * arrived `SWYM_GIVE_UP_MS` into the page load.
 */
export const watchSwymLoad = () => {
  if (watching) return;
  watching = true;

  setTimeout(() => markSwymUnavailable(null), SWYM_LOAD_DEADLINE_MS);

  setTimeout(
    () => {
      if (window._swat) return;
      markSwymUnavailable('missing');
    },
    Math.max(0, SWYM_GIVE_UP_MS - performance.now())
  );
};

/**
 * Records that Swym answered: forgets any remembered failure and shows the hearts.
 * @param {{ showOnThisPage?: boolean }} [options] Defaults to showing unless the page
 *   started unavailable.
 */
export const markSwymReady = ({
  showOnThisPage = !startedUnavailable,
} = {}) => {
  window.swymLoaded = true;

  const config = window.swymUnavailable;
  try {
    if (config) window.localStorage.removeItem(config.key);
  } catch (_) {
    // Storage blocked: nothing was remembered.
  }

  if (!showOnThisPage) return;
  document.documentElement.dataset.swym = 'ready';
};
